const MAGIC = new TextEncoder().encode('NNENC1');
const ITERATIONS = 310_000;

function concat(...parts: Uint8Array[]): Uint8Array {
  const output = new Uint8Array(parts.reduce((sum, part) => sum + part.length, 0));
  let offset = 0;
  for (const part of parts) { output.set(part, offset); offset += part.length; }
  return output;
}

async function deriveKey(passphrase: string, salt: Uint8Array, usages: KeyUsage[]): Promise<CryptoKey> {
  if (passphrase.length < 10) throw new Error('Use a passphrase with at least 10 characters.');
  const material = await crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations: ITERATIONS }, material, { name: 'AES-GCM', length: 256 }, false, usages);
}

export async function encryptProject(bytes: Uint8Array, passphrase: string): Promise<Uint8Array> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt, ['encrypt']);
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, bytes as BufferSource));
  const header = new TextEncoder().encode(JSON.stringify({ algorithm: 'AES-256-GCM', kdf: 'PBKDF2-SHA-256', iterations: ITERATIONS, salt: Array.from(salt), iv: Array.from(iv) }));
  const length = new Uint8Array(4);
  new DataView(length.buffer).setUint32(0, header.length, false);
  return concat(MAGIC, length, header, ciphertext);
}

export function isEncryptedProject(bytes: Uint8Array): boolean {
  return MAGIC.every((value, index) => bytes[index] === value);
}

export async function decryptProject(bytes: Uint8Array, passphrase: string): Promise<Uint8Array> {
  if (!isEncryptedProject(bytes)) throw new Error('Not an encrypted NumberNode project.');
  const headerLength = new DataView(bytes.buffer, bytes.byteOffset + MAGIC.length, 4).getUint32(0, false);
  const headerStart = MAGIC.length + 4;
  const header = JSON.parse(new TextDecoder().decode(bytes.slice(headerStart, headerStart + headerLength))) as { salt: number[]; iv: number[] };
  const key = await deriveKey(passphrase, new Uint8Array(header.salt), ['decrypt']);
  try {
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: new Uint8Array(header.iv) as BufferSource }, key, bytes.slice(headerStart + headerLength) as BufferSource);
    return new Uint8Array(plain);
  } catch {
    throw new Error('The passphrase is incorrect or the encrypted project is damaged.');
  }
}
